import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaSpinner } from "react-icons/fa";
import { setDischargeField } from "../../../../Redux/actions/dischargeActions";
import DischargeService from "../../../../Services/DischargeService";

const MASTER_PAYLOAD = { activeYn: "Y", pageNo: 1, pageSize: 30 };

export default function Disposition() {
  const dispatch = useDispatch();
  const dischargeDispositionDtls = useSelector(
    (state) => state.discharge?.dischargeDispositionDtls || []
  );
  const dispositionId = useSelector(
    (state) => state.discharge?.dispositionId || ""
  );
  const dispositionCode = useSelector(
    (state) => state.discharge?.dispositionCode || ""
  );
  const dispositionDesc = useSelector(
    (state) => state.discharge?.dispositionDesc || ""
  );
  const additionalDispositionNotes = useSelector(
    (state) =>
      state.discharge?.additionalDispositionNotes ||
      state.discharge?.dispositionNotes ||
      ""
  );

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    DischargeService.GetDischargeDispositionMaster(MASTER_PAYLOAD)
      .then((res) => {
        const data = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : [];
        // Sort by displayOrder if available
        data.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        setOptions(data);
        console.log("res", res);
      })
      .catch((err) => console.error("Disposition fetch error:", err))
      .finally(() => setLoading(false));
  }, []);

  const isChecked = (disp) => {
    const id = disp.dispositionId ?? disp.id;
    const code = disp.dispositionCode || disp.code;
    const name = disp.dispositionName || disp.dispositionDesc || disp.description || disp.name || "";

    if (Array.isArray(dischargeDispositionDtls) && dischargeDispositionDtls.length > 0) {
      return dischargeDispositionDtls.some(
        (item) =>
          item.dispositionId === id ||
          item.dispositionCode === code ||
          item.dispositionName === name
      );
    }

    // Fallback matching against legacy Redux fields
    return (
      dispositionDesc === name ||
      dispositionId === id ||
      dispositionCode === code ||
      (name === "Home" && dispositionDesc === "Discharged Home")
    );
  };

  const handleToggleDisposition = (disp) => {
    const id = disp.dispositionId ?? disp.id;
    const code = disp.dispositionCode || disp.code || id;
    const name = disp.dispositionName || disp.dispositionDesc || disp.description || disp.name || "";

    let currentList = [];
    if (Array.isArray(dischargeDispositionDtls) && dischargeDispositionDtls.length > 0) {
      currentList = [...dischargeDispositionDtls];
    } else if (dispositionDesc || dispositionId) {
      const initialMatch = options.find(
        (o) =>
          (o.dispositionName || o.dispositionDesc || o.name) === dispositionDesc ||
          (o.dispositionId ?? o.id) === dispositionId ||
          ((o.dispositionName || o.name) === "Home" && dispositionDesc === "Discharged Home")
      );
      if (initialMatch) {
        currentList = [
          {
            dispositionId: initialMatch.dispositionId ?? initialMatch.id,
            dispositionCode: initialMatch.dispositionCode || initialMatch.code,
            dispositionName: initialMatch.dispositionName || initialMatch.dispositionDesc || initialMatch.name,
            status: 1,
          },
        ];
      }
    }

    const existingIndex = currentList.findIndex(
      (item) =>
        item.dispositionId === id ||
        item.dispositionCode === code ||
        item.dispositionName === name
    );

    let updatedList = [];
    if (existingIndex > -1) {
      updatedList = currentList.filter((_, idx) => idx !== existingIndex);
    } else {
      updatedList = [
        ...currentList,
        {
          dispositionId: id,
          dispositionCode: code,
          dispositionName: name,
          status: 1,
        },
      ];
    }

    dispatch(setDischargeField("dischargeDispositionDtls", updatedList));
    dispatch(setDischargeField("selectedDispositions", updatedList));
    dispatch(
      setDischargeField(
        "dispositionId",
        updatedList.length > 0 ? updatedList.map((d) => d.dispositionId).join(", ") : ""
      )
    );
    dispatch(
      setDischargeField(
        "dispositionCode",
        updatedList.length > 0 ? updatedList.map((d) => d.dispositionCode).join(", ") : ""
      )
    );
    dispatch(
      setDischargeField(
        "dispositionDesc",
        updatedList.length > 0 ? updatedList.map((d) => d.dispositionName).join(", ") : ""
      )
    );
  };

  const selectedCount =
    Array.isArray(dischargeDispositionDtls) && dischargeDispositionDtls.length > 0
      ? dischargeDispositionDtls.length
      : dispositionDesc
      ? 1
      : 0;

  return (
    <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
      <h3 className="text-sm font-bold text-teal-700 mb-3 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-teal-600"></span>
        12. Disposition {selectedCount > 0 ? `(${selectedCount} Selected)` : ""}
      </h3>

      {loading && (
        <div className="flex items-center gap-2 py-3 text-[12px] text-gray-400">
          <FaSpinner className="animate-spin" /> Loading disposition options...
        </div>
      )}

      {!loading && options.length === 0 && (
        <p className="text-[12px] text-gray-400 italic py-2">
          No disposition options available.
        </p>
      )}

      {!loading && options.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs font-medium text-gray-700">
          {options.map((disp) => {
            const id = disp.dispositionId ?? disp.id;
            const code = disp.dispositionCode || disp.code || id;
            const name =
              disp.dispositionName ||
              disp.dispositionDesc ||
              disp.description ||
              disp.name ||
              "";
            const checked = isChecked(disp);

            return (
              <label
                key={code || id}
                className="flex items-center gap-2 cursor-pointer hover:text-teal-800 transition"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleToggleDisposition(disp)}
                  className="rounded text-teal-600 focus:ring-teal-500 w-4 h-4 cursor-pointer"
                />
                <span className={checked ? "font-semibold text-teal-900" : ""}>
                  {name}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {/* Additional Disposition Notes */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <label className="block text-xs font-semibold mb-1 text-gray-700">
          Additional Disposition Notes
        </label>
        <div className="border border-gray-200 rounded-md overflow-hidden bg-white focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500 transition">
          <textarea
            value={additionalDispositionNotes}
            onChange={(e) => {
              const val = e.target.value;
              dispatch(setDischargeField("additionalDispositionNotes", val));
              dispatch(setDischargeField("dispositionNotes", val));
            }}
            rows={3}
            className="w-full p-2.5 text-xs text-gray-700 outline-none resize-none"
            placeholder="Enter additional disposition notes (e.g. transfer hospital details, transport arrangements, escort instructions)..."
          ></textarea>
        </div>
      </div>
    </div>
  );
}

